import { Box, Button, Flex, Heading, useToast, VStack } from '@chakra-ui/react';
import * as _ from 'lodash';
import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { finleyAssert } from '@finley-technologies/common';

import { FundingRequestStatusEnumGQLType } from 'types/dashboard-core-gqlTypes.generated.d';
import {
  BankAccountMap,
  FundingRequestComponentContainer,
} from 'types/fundingRequests';

import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { useCancelFundingRequestMutation } from '~/borrower/domains/fundingRequests/requests/fundingRequests.gql.generated';
import { useFundingRequestContext } from '~/borrower/domains/fundingRequests/ui/manage/Context';
import { FundingRequestComponentsTable } from '~/borrower/domains/fundingRequests/ui/shared/ComponentsTable';
import { FundingRequestSideColumn } from '~/borrower/domains/fundingRequests/ui/shared/ContainerSections';
import { GetEventsDocumentGQLType } from '~/core/routes/events/legacy/events.gql.generated';
import { PageHeader } from '~/packages/common-frontend/components/PageContainer';
import {
  errorToastConfig,
  errorToastId,
  successToastConfig,
} from '~/packages/common-frontend/constants';
import { useCreditFacilityContext } from '~/packages/credit-facility/hooks/CreditFacilityProvider';
import { GetFundingRequestsWithBaseDetailsDocumentGQLType } from '~/packages/funding-requests/requests/requests.gql.generated';
import { FundingRequestReports } from '~/packages/funding-requests/ui/FundingRequestReports';
import { FundingRequestEventHistory } from '~/packages/funding-requests/ui/shared/FundingRequestEvents';

const monitorStatusLabels: {
  [key in FundingRequestStatusEnumGQLType]?: {
    headerLabel: string;
    subheaderLabel: string;
  };
} = {
  approved: {
    headerLabel: 'Funding request approved',
    subheaderLabel: 'Your request has been approved.',
  },
  canceled: {
    headerLabel: 'Funding request canceled',
    subheaderLabel: '',
  },
  expired: {
    headerLabel: 'Funding request expired',
    subheaderLabel: '',
  },
  rejected: {
    headerLabel: 'Funding request rejected',
    subheaderLabel: '',
  },
  submitted: {
    headerLabel: 'Funding request submitted',
    subheaderLabel:
      'Your request has been submitted. We’ll update this page as soon as this request is approved.',
  },
};

const FundingRequestMonitorMain = ({
  status,
  fundingRequestComponents,
  isCollectionsRecycleEnabled,
  isVoluntaryPaydownAllowed,
  maxFundingRequestAmount,
  bankAccountMap,
}: {
  status: FundingRequestStatusEnumGQLType;
  fundingRequestComponents: FundingRequestComponentContainer;
  isCollectionsRecycleEnabled: boolean;
  isVoluntaryPaydownAllowed: boolean;
  maxFundingRequestAmount: number;
  bankAccountMap: BankAccountMap;
}) => {
  const statusLabel = monitorStatusLabels[status];
  if (!statusLabel) {
    return <></>;
  }
  return (
    <VStack spacing={4} alignItems="left">
      <PageHeader
        headerLabel={statusLabel.headerLabel}
        subheaderLabel={statusLabel.subheaderLabel}
      />
      <FundingRequestComponentsTable
        fundingRequestComponents={fundingRequestComponents}
        isCollectionsRecycleEnabled={isCollectionsRecycleEnabled}
        isVoluntaryPaydownAllowed={isVoluntaryPaydownAllowed}
        maxFundingRequestAmount={maxFundingRequestAmount}
        bankAccountMap={bankAccountMap}
      />
    </VStack>
  );
};

export const FundingRequestMonitorViewBody = () => {
  const toast = useToast();
  const { state, fundingRequestId } = useFundingRequestContext();
  const { creditFacilityId } = useCreditFacilityContext();
  const navigate = useNavigate();
  const {
    _status,
    components,
    creditFacilityName,
    bankAccountMap,
    fundingRequestEvents,
    fundingRequestReports,
    constraints: {
      _masterConstraints: {
        isVoluntaryPaydownAllowed,
        isCollectionsRecycleEnabled,
        maxFundingRequestAmount,
      },
    },
  } = state;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [cancelFundingRequestMutate] = useCancelFundingRequestMutation({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    refetchQueries: [
      {
        query: GetFundingRequestsWithBaseDetailsDocumentGQLType,
        variables: { creditFacilityIds: [creditFacilityId] },
      },
      GetEventsDocumentGQLType,
    ],
  });

  const handleCancel = useCallback(async () => {
    finleyAssert(
      _status === 'submitted',
      "Funding request status must be in 'submitted' status to cancel.",
    );
    try {
      setIsSubmitting(true);
      const { data } = await cancelFundingRequestMutate({
        variables: {
          fundingRequestId,
        },
      });

      if (data) {
        successToastConfig.title = 'Canceled funding request successfully';
        toast(successToastConfig);
        navigate('/funding-requests');
      }
    } catch (err: any) {
      if (!toast.isActive(errorToastId)) {
        errorToastConfig.title = `Error canceling funding request`;
        toast(errorToastConfig);
        setIsSubmitting(false);
      }
      throw err;
    }
  }, [_status, cancelFundingRequestMutate, fundingRequestId, navigate, toast]);

  const canCancel = _status === 'submitted';

  const ButtonRow = canCancel ? (
    <Button
      isLoading={isSubmitting}
      color="white"
      backgroundColor="#BFBEBF"
      size="lg"
      borderRadius="0"
      onClick={handleCancel}
    >
      Cancel submission ⨉
    </Button>
  ) : (
    <></>
  );
  return (
    <Flex justify="flex-start" width="auto" flexGrow={1}>
      <Box m={2} flex="5">
        <FundingRequestMonitorMain
          status={_status}
          fundingRequestComponents={components}
          isCollectionsRecycleEnabled={isCollectionsRecycleEnabled}
          isVoluntaryPaydownAllowed={isVoluntaryPaydownAllowed}
          maxFundingRequestAmount={maxFundingRequestAmount}
          bankAccountMap={bankAccountMap}
        />
        {fundingRequestReports && !_.isEmpty(fundingRequestReports) && (
          <FundingRequestReports reports={fundingRequestReports} />
        )}
        {fundingRequestEvents && !_.isEmpty(fundingRequestEvents) && (
          <>
            <Heading
              as="h3"
              fontSize="2xl"
              paddingTop="10px"
              mt="35px"
              mb="25px"
            >
              Event History
            </Heading>
            <FundingRequestEventHistory
              bankAccountsMap={bankAccountMap}
              events={fundingRequestEvents}
              creditFacilityName={creditFacilityName}
            />
          </>
        )}
      </Box>
      <FundingRequestSideColumn
        ButtonRow={ButtonRow}
        FundingRequestInfoBox={null}
      />
    </Flex>
  );
};
